import { useState, useRef, useCallback, useEffect } from 'react'
import { View, TextInput, Text, Pressable, FlatList, StyleSheet, Platform } from 'react-native'
import { MapPin } from 'lucide-react-native'
import { colors, spacing, borderRadius, typography } from '../theme'

interface AddressInputProps {
  value: string
  onChange: (val: string) => void
  placeholder?: string
  error?: boolean
}

interface Suggestion {
  tekst: string
}

export default function AddressInput({ value, onChange, placeholder, error }: AddressInputProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [open, setOpen] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      if (debounce.current) clearTimeout(debounce.current)
    }
  }, [])

  const search = useCallback((q: string) => {
    if (debounce.current) clearTimeout(debounce.current)
    const normalized = q.trim().replace(/\s+/g, ' ')
    if (normalized.length < 2) {
      setSuggestions([])
      setOpen(false)
      return
    }
    debounce.current = setTimeout(async () => {
      abortRef.current?.abort()
      abortRef.current = new AbortController()
      try {
        const res = await fetch(
          `https://api.dataforsyningen.dk/adresser/autocomplete?q=${encodeURIComponent(normalized)}&per_side=5&fuzzy=`,
          { signal: abortRef.current.signal }
        )
        if (!res.ok) { setSuggestions([]); return }
        const data = await res.json()
        if (!Array.isArray(data)) { setSuggestions([]); return }
        setSuggestions(data)
        setOpen(data.length > 0)
      } catch {
        setSuggestions([])
      }
    }, 250)
  }, [])

  return (
    <View style={styles.container}>
      <TextInput
        style={[styles.input, error && { borderColor: '#ef4444' }]}
        value={value}
        onChangeText={(t) => { onChange(t); search(t) }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder || 'Søg adresse...'}
        placeholderTextColor={colors.text.muted}
        autoCorrect={false}
      />
      {open && suggestions.length > 0 && (
        <View style={styles.dropdown}>
          {suggestions.map((s, i) => (
            <Pressable
              key={i}
              style={styles.suggestion}
              onPress={() => { onChange(s.tekst); setOpen(false); setSuggestions([]) }}
            >
              <MapPin size={14} color={colors.text.muted} />
              <Text style={styles.suggestionText} numberOfLines={1}>{s.tekst}</Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 20 },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    ...typography.body,
    color: colors.text.primary,
    backgroundColor: colors.background,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    marginTop: 4,
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15, shadowRadius: 8 },
      android: { elevation: 4 },
      default: {},
    }),
    maxHeight: 200,
    overflow: 'hidden',
  },
  suggestion: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  suggestionText: {
    ...typography.body,
    color: colors.text.primary,
    flex: 1,
    fontSize: 14,
  },
})
